import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Plus, Trash2, GripVertical, Search, Filter } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { videoApi, occasionApi, type Video, type Occasion } from '../services/api';

interface VideoSelectorProps {
  occasion: Occasion;
  onClose: () => void;
}

interface SelectedVideoItemProps {
  video: Video;
  onRemove: (videoId: string) => void;
}

function SelectedVideoItem({ video, onRemove }: SelectedVideoItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: video.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white border border-gray-200 rounded-lg p-3 flex items-center gap-3"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
      >
        <GripVertical className="w-5 h-5" />
      </button>

      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-gray-900 text-sm truncate">{video.title}</h4>
        <div className="flex gap-2 mt-1">
          <span className="text-xs text-gray-500">{video.category}</span>
          <span className="text-xs text-gray-500">•</span>
          <span className="text-xs text-gray-500">{video.difficulty}</span>
          {video.duration_seconds > 0 && (
            <>
              <span className="text-xs text-gray-500">•</span>
              <span className="text-xs text-gray-500">{formatDuration(video.duration_seconds)}</span>
            </>
          )}
        </div>
      </div>

      <button
        onClick={() => onRemove(video.id)}
        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
        title="Remove video"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}

interface VideoLibraryCardProps {
  video: Video;
  isSelected: boolean;
  onSelect: (video: Video) => void;
}

function VideoLibraryCard({ video, isSelected, onSelect }: VideoLibraryCardProps) {
  const categoryColors = {
    strength: 'bg-red-100 text-red-800',
    cardio: 'bg-blue-100 text-blue-800',
    mobility: 'bg-green-100 text-green-800',
    flexibility: 'bg-purple-100 text-purple-800',
    other: 'bg-gray-100 text-gray-800',
  };

  const difficultyColors = {
    beginner: 'bg-green-100 text-green-800',
    intermediate: 'bg-yellow-100 text-yellow-800',
    advanced: 'bg-red-100 text-red-800',
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div
      onClick={() => !isSelected && onSelect(video)}
      className={`bg-white border-2 rounded-lg p-3 cursor-pointer transition-all ${
        isSelected
          ? 'border-indigo-600 bg-indigo-50 cursor-not-allowed opacity-60'
          : 'border-gray-200 hover:border-indigo-400 hover:shadow-md'
      }`}
    >
      <div className="flex items-start gap-3">
        {video.thumbnail_url ? (
          <img
            src={video.thumbnail_url}
            alt={video.title}
            className="w-20 h-20 object-cover rounded flex-shrink-0"
          />
        ) : (
          <div className="w-20 h-20 bg-gray-200 rounded flex-shrink-0 flex items-center justify-center">
            <span className="text-gray-400 text-xs">No image</span>
          </div>
        )}

        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 text-sm truncate">{video.title}</h4>
          {video.description && (
            <p className="text-xs text-gray-600 line-clamp-2 mt-1">{video.description}</p>
          )}

          <div className="flex flex-wrap gap-1 mt-2">
            <span className={`text-xs px-2 py-0.5 rounded-full ${categoryColors[video.category]}`}>
              {video.category}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${difficultyColors[video.difficulty]}`}>
              {video.difficulty}
            </span>
            {video.duration_seconds > 0 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-800">
                {formatDuration(video.duration_seconds)}
              </span>
            )}
          </div>
        </div>

        {isSelected && (
          <div className="flex-shrink-0">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-600 text-white">
              Selected
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function VideoSelector({ occasion, onClose }: VideoSelectorProps) {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Fetch all videos for selection
  const { data: allVideos = [] } = useQuery({
    queryKey: ['videos', categoryFilter, difficultyFilter, searchQuery],
    queryFn: async () => {
      const params: any = {};
      if (categoryFilter) params.category = categoryFilter;
      if (difficultyFilter) params.difficulty = difficultyFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await videoApi.list(params);
      const data = response.data;
      // Backend returns paginated { data: [...], ... } by default; extract the array
      return Array.isArray(data) ? data : (data?.data ?? []);
    },
  });

  // Fetch currently selected videos for this occasion
  const { data: selectedVideos = [], refetch: refetchSelectedVideos } = useQuery({
    queryKey: ['occasionVideos', occasion.id],
    queryFn: async () => {
      const response = await occasionApi.getVideos(occasion.id);
      return response.data;
    },
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Attach video mutation
  const attachVideoMutation = useMutation({
    mutationFn: ({ videoId, order }: { videoId: string; order: number }) =>
      occasionApi.attachVideo(occasion.id, videoId, order),
    onSuccess: () => {
      refetchSelectedVideos();
      queryClient.invalidateQueries({ queryKey: ['occasions'] });
    },
  });

  // Detach video mutation
  const detachVideoMutation = useMutation({
    mutationFn: (videoId: string) => occasionApi.detachVideo(occasion.id, videoId),
    onSuccess: () => {
      refetchSelectedVideos();
      queryClient.invalidateQueries({ queryKey: ['occasions'] });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = selectedVideos.findIndex((v) => v.id === active.id);
      const newIndex = selectedVideos.findIndex((v) => v.id === over.id);

      const reorderedVideos = arrayMove(selectedVideos, oldIndex, newIndex);

      // Update order for each video
      reorderedVideos.forEach((video, index) => {
        occasionApi.attachVideo(occasion.id, video.id, index);
      });

      // Optimistically update the UI
      queryClient.setQueryData(['occasionVideos', occasion.id], reorderedVideos);
    }
  };

  const handleSelectVideo = (video: Video) => {
    const nextOrder = selectedVideos.length;
    attachVideoMutation.mutate({ videoId: video.id, order: nextOrder });
  };

  const handleRemoveVideo = (videoId: string) => {
    detachVideoMutation.mutate(videoId);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setCategoryFilter('');
    setDifficultyFilter('');
  };

  const hasActiveFilters = searchQuery || categoryFilter || difficultyFilter;
  const selectedVideoIds = new Set(selectedVideos.map((v) => v.id));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Manage Videos</h2>
            <p className="text-sm text-gray-600 mt-1">{occasion.title}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Video Library (Left Side) */}
          <div className="flex-1 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-medium text-gray-900 mb-3">Video Library</h3>

              {/* Search and Filters */}
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search videos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-md transition-colors w-full justify-center ${
                    showFilters || hasActiveFilters
                      ? 'border-indigo-600 text-indigo-600 bg-indigo-50'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Filter className="w-4 h-4" />
                  Filters
                  {hasActiveFilters && (
                    <span className="bg-indigo-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {[categoryFilter, difficultyFilter].filter(Boolean).length}
                    </span>
                  )}
                </button>

                {showFilters && (
                  <div className="space-y-3 pt-2">
                    <div>
                      <label htmlFor="video-category" className="block text-xs font-medium text-gray-700 mb-1">
                        Category
                      </label>
                      <select
                        id="video-category"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        <option value="">All Categories</option>
                        <option value="strength">Strength</option>
                        <option value="cardio">Cardio</option>
                        <option value="mobility">Mobility</option>
                        <option value="flexibility">Flexibility</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="video-difficulty" className="block text-xs font-medium text-gray-700 mb-1">
                        Difficulty
                      </label>
                      <select
                        id="video-difficulty"
                        value={difficultyFilter}
                        onChange={(e) => setDifficultyFilter(e.target.value)}
                        className="block w-full text-sm rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      >
                        <option value="">All Difficulties</option>
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>

                    {hasActiveFilters && (
                      <button
                        onClick={clearFilters}
                        className="text-xs text-indigo-600 hover:text-indigo-800"
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Video List */}
            <div className="flex-1 overflow-y-auto p-4">
              {allVideos.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  {hasActiveFilters ? 'No videos found matching your filters' : 'No videos available'}
                </div>
              ) : (
                <div className="space-y-2">
                  {allVideos.map((video) => (
                    <VideoLibraryCard
                      key={video.id}
                      video={video}
                      isSelected={selectedVideoIds.has(video.id)}
                      onSelect={handleSelectVideo}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Selected Videos (Right Side) */}
          <div className="w-96 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-medium text-gray-900">
                Selected Videos ({selectedVideos.length})
              </h3>
              <p className="text-xs text-gray-600 mt-1">Drag to reorder</p>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {selectedVideos.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  <Plus className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>No videos selected</p>
                  <p className="text-xs mt-1">Click videos from the library to add them</p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={selectedVideos.map((v) => v.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {selectedVideos.map((video) => (
                        <SelectedVideoItem
                          key={video.id}
                          video={video}
                          onRemove={handleRemoveVideo}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
